import { useState } from 'react';
import { Alert, SpeedDial, SpeedDialAction, useTheme } from '@mui/material';
import { PostAdd, Search, Summarize } from '@mui/icons-material';
import { AutoAwesomeOutlined as SparklesIcon } from '@mui/icons-material';
import { toggleChatGPTAssistant, toggleChatGPTAddHandOver, toggleChatGPTHandoverSummarizer } from '../redux/ai/aiSlice';
import HandoverChatbot from './HandoverChatbot';
import ChatGPTAddHandover from './chat-gpt-parser/ChatGPTParser';
import ChatGPTHandoverSummarizer from './chat-gpt-summarizer/ChatGPTSummarizer';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import { useNavigate } from 'react-router-dom';
import { useGetMyCompanyQuery } from '../redux/company/companyApi';
import { getCompanyAiState } from '../helpers/companyAiHelper';

const ChatGPTSpeedDial = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isChatGPTAssistantOpen = useSelector((state: RootState) => state.ai.isChatGPTAssistantOpen);
  const isChatGPTAddHandOverOpen = useSelector((state: RootState) => state.ai.isChatGPTAddHandOverOpen);
  const isChatGPTHandoverSummarizerOpen = useSelector((state: RootState) => state.ai.isChatGPTHandoverSummarizerOpen);

  // Check if AI feature is enabled from health check
  const isAIEnabled = useSelector((state: RootState) => state.health.features.ai);
  const { data: companyWorkspace } = useGetMyCompanyQuery();
  const aiState = getCompanyAiState(companyWorkspace?.company);

  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [lockedMessage, setLockedMessage] = useState<string | null>(null);

  // Don't render anything if AI feature is disabled
  if (!isAIEnabled) {
    return null;
  }

  const handleAIAssistantClick = () => {
    if (!aiState.canUseAi) {
      setLockedMessage(aiState.alertMessage || 'AI is not available for this workspace right now.');
      return;
    }

    dispatch(toggleChatGPTAssistant());
  };

  // Function to handle the "AI Add Handover" click
  const handleAddHandoverClick = () => {
    if (!aiState.canUseAi) {
      setLockedMessage(aiState.alertMessage || 'AI is not available for this workspace right now.');
      return;
    }

    navigate('/handovers');
    dispatch(toggleChatGPTAddHandOver());
  };

  const handleHandoverSummarizeClick = () => {
    if (!aiState.canUseAi) {
      setLockedMessage(aiState.alertMessage || 'AI is not available for this workspace right now.');
      return;
    }

    dispatch(toggleChatGPTHandoverSummarizer());
  };

  const handleSearchClick = () => {
    if (!aiState.canUseAi) {
      setLockedMessage(aiState.alertMessage || 'AI is not available for this workspace right now.');
    }
  };

  const actions = [
    {
      icon: <SparklesIcon style={{ width: 24, height: 24 }} />,
      name: 'AI Assistant',
      onClick: handleAIAssistantClick,
    },
    {
      icon: <PostAdd />,
      name: 'AI Parser',
      onClick: handleAddHandoverClick,
    },
    {
      icon: <Summarize />,
      name: 'AI Summarizer',
      onClick: handleHandoverSummarizeClick,
    },
    {
      icon: <Search />,
      name: 'AI Search',
      onClick: handleSearchClick,
    },
  ];

  return (
    <>
      <SpeedDial
        ariaLabel="SpeedDial example"
        icon={<SparklesIcon style={{ width: 24, height: 24 }} />}
        open={open}
        onClose={() => setOpen(false)}
        onOpen={() => setOpen(true)}
        direction="up"
        sx={{
          position: 'fixed',
          bottom: { xs: 8, sm: 16 },
          right: { xs: 8, sm: 16 },
          zIndex: 1250,
          '& .MuiSpeedDial-fab': {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
            },
            width: { xs: 48, sm: 56 },
            height: { xs: 48, sm: 56 },
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25)',
          },
          '& .MuiSpeedDialAction-fab': {
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.primary.main,
            width: { xs: 40, sm: 48 },
            height: { xs: 40, sm: 48 },
            '&:hover': {
              backgroundColor: theme.palette.primary.light,
              color: theme.palette.primary.contrastText,
            },
          },
        }}
      >
        {actions.map((action, index) => (
          <SpeedDialAction
            key={index}
            icon={action.icon}
            tooltipTitle={aiState.canUseAi ? action.name : `${action.name}: ${aiState.alertMessage || 'Locked'}`}
            onClick={action.onClick}
          />
        ))}
      </SpeedDial>
      {lockedMessage && (
        <Alert
          severity={aiState.alertSeverity || 'info'}
          onClose={() => setLockedMessage(null)}
          sx={{
            position: 'fixed',
            right: { xs: 8, sm: 16 },
            bottom: { xs: 72, sm: 88 },
            zIndex: 1250,
            maxWidth: 360,
          }}
        >
          {lockedMessage}
          {aiState.isTrial || aiState.isUsageLimitReached ? ' Visit Billing to upgrade or add more usage.' : ''}
        </Alert>
      )}

      {/* Conditionally render both windows based on state */}
      {isChatGPTAssistantOpen && <HandoverChatbot onClose={() => dispatch(toggleChatGPTAssistant())} />}
      {isChatGPTAddHandOverOpen && <ChatGPTAddHandover onClose={() => dispatch(toggleChatGPTAddHandOver())} />}
      {isChatGPTHandoverSummarizerOpen && (
        <ChatGPTHandoverSummarizer onClose={() => dispatch(toggleChatGPTHandoverSummarizer())} />
      )}
    </>
  );
};

export default ChatGPTSpeedDial;
